import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type AppUpdateCopy = {
  title: string;
  description: string;
  reload: string;
  blocked: string;
  dismiss: string;
};

const APP_UPDATE_COPY: Record<SupportedLocale, AppUpdateCopy> = {
  en: {
    dismiss: 'Later',
    blocked:
      'A new version is ready. Finish sending or save your changes before reloading.',
    title: 'IMAI was updated',
    description:
      'A new version is available. Reload to get the latest version.',
    reload: 'Reload',
  },
  ar: {
    dismiss: 'لاحقًا',
    blocked:
      'الإصدار الجديد جاهز. أكمل الإرسال أو احفظ تغييراتك قبل إعادة التحميل.',
    title: 'تم تحديث IMAI',
    description: 'يتوفر إصدار جديد. أعد التحميل للحصول على أحدث إصدار.',
    reload: 'إعادة التحميل',
  },
  es: {
    dismiss: 'Más tarde',
    blocked:
      'Hay una nueva versión lista. Termina de enviar o guarda los cambios antes de recargar.',
    title: 'IMAI se actualizó',
    description:
      'Hay una nueva versión disponible. Recarga para obtener la versión más reciente.',
    reload: 'Recargar',
  },
  'pt-BR': {
    dismiss: 'Depois',
    blocked:
      'Uma nova versão está pronta. Conclua o envio ou salve suas alterações antes de recarregar.',
    title: 'O IMAI foi atualizado',
    description:
      'Uma nova versão está disponível. Recarregue para usar a versão mais recente.',
    reload: 'Recarregar',
  },
  fr: {
    dismiss: 'Plus tard',
    blocked:
      'Une nouvelle version est prête. Terminez l’envoi ou enregistrez vos modifications avant de recharger.',
    title: 'IMAI a été mis à jour',
    description:
      'Une nouvelle version est disponible. Rechargez pour obtenir la dernière version.',
    reload: 'Recharger',
  },
  it: {
    dismiss: 'Più tardi',
    blocked:
      'Una nuova versione è pronta. Completa l’invio o salva le modifiche prima di ricaricare.',
    title: 'IMAI è stato aggiornato',
    description:
      'È disponibile una nuova versione. Ricarica per ottenere la versione più recente.',
    reload: 'Ricarica',
  },
  ko: {
    dismiss: '나중에',
    blocked:
      '새 버전이 준비되었습니다. 전송을 마치거나 변경 사항을 저장한 후 새로고침하세요.',
    title: 'IMAI가 업데이트되었습니다',
    description:
      '새 버전을 사용할 수 있습니다. 최신 버전을 사용하려면 새로고침하세요.',
    reload: '새로고침',
  },
  de: {
    dismiss: 'Später',
    blocked:
      'Eine neue Version ist bereit. Beende das Senden oder speichere deine Änderungen vor dem Neuladen.',
    title: 'IMAI wurde aktualisiert',
    description:
      'Eine neue Version ist verfügbar. Lade die Seite neu, um die neueste Version zu erhalten.',
    reload: 'Neu laden',
  },
  ja: {
    dismiss: '後で',
    blocked:
      '新しいバージョンの準備ができました。送信を完了するか変更を保存してから再読み込みしてください。',
    title: 'IMAI が更新されました',
    description:
      '新しいバージョンが利用可能です。再読み込みして最新バージョンを取得してください。',
    reload: '再読み込み',
  },
  hi: {
    dismiss: 'बाद में',
    blocked:
      'नया संस्करण तैयार है। फिर से लोड करने से पहले भेजना पूरा करें या बदलाव सहेजें।',
    title: 'IMAI अपडेट हो गया है',
    description:
      'नया संस्करण उपलब्ध है। नवीनतम संस्करण पाने के लिए पेज फिर से लोड करें।',
    reload: 'फिर से लोड करें',
  },
  ru: {
    dismiss: 'Позже',
    blocked:
      'Новая версия готова. Завершите отправку или сохраните изменения перед перезагрузкой.',
    title: 'IMAI обновлён',
    description:
      'Доступна новая версия. Перезагрузите страницу, чтобы получить последнюю версию.',
    reload: 'Перезагрузить',
  },
  zh: {
    dismiss: '稍后',
    blocked: '新版本已就绪。请完成发送或保存更改后再重新加载。',
    title: 'IMAI 已更新',
    description: '有新版本可用。请重新加载以获取最新版本。',
    reload: '重新加载',
  },
  'zh-TW': {
    dismiss: '稍後',
    blocked: '新版本已就緒。請完成傳送或儲存變更後再重新載入。',
    title: 'IMAI 已更新',
    description: '有新版本可用。請重新載入以取得最新版本。',
    reload: '重新載入',
  },
  vi: {
    dismiss: 'Để sau',
    blocked:
      'Phiên bản mới đã sẵn sàng. Hãy gửi xong hoặc lưu thay đổi trước khi tải lại.',
    title: 'IMAI đã được cập nhật',
    description: 'Đã có phiên bản mới. Tải lại để dùng phiên bản mới nhất.',
    reload: 'Tải lại',
  },
  id: {
    dismiss: 'Nanti',
    blocked:
      'Versi baru siap. Selesaikan pengiriman atau simpan perubahan sebelum memuat ulang.',
    title: 'IMAI telah diperbarui',
    description:
      'Versi baru tersedia. Muat ulang untuk mendapatkan versi terbaru.',
    reload: 'Muat ulang',
  },
};

export function getAppUpdateCopy(
  locale: string | null | undefined
): AppUpdateCopy {
  return APP_UPDATE_COPY[normalizeLocale(locale)];
}
